import { useState, useEffect, useCallback } from 'react';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  model?: string;
  provider?: string;
}

interface UseChatHistoryOptions {
  storageKey?: string;
  maxMessages?: number;
}

const DEFAULT_STORAGE_KEY = 'biznesbezklikania_chat_history';
const DEFAULT_MAX_MESSAGES = 100;

export function useChatHistory(options: UseChatHistoryOptions = {}) {
  const {
    storageKey = DEFAULT_STORAGE_KEY,
    maxMessages = DEFAULT_MAX_MESSAGES,
  } = options;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load messages from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setMessages(parsed);
        }
      }
    } catch (error) {
      console.warn('Failed to load chat history:', error);
    }
    setIsLoaded(true);
  }, [storageKey]);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (!isLoaded) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch (error) {
      console.warn('Failed to save chat history:', error);
    }
  }, [messages, storageKey, isLoaded]);

  const addMessage = useCallback((
    message: Omit<ChatMessage, 'id' | 'timestamp'>
  ): ChatMessage => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: Date.now(),
    };

    setMessages((prev) => {
      const updated = [...prev, newMessage];
      // Trim to max messages if needed
      if (updated.length > maxMessages) {
        return updated.slice(-maxMessages);
      }
      return updated;
    });

    return newMessage;
  }, [maxMessages]);

  const updateMessage = useCallback((
    id: string,
    updates: Partial<Omit<ChatMessage, 'id'>>
  ) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  const deleteMessage = useCallback((id: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  const clearHistory = useCallback(() => {
    setMessages([]);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear chat history:', error);
    }
  }, [storageKey]);

  const getLastUserMessage = useCallback(() => {
    return [...messages].reverse().find((msg) => msg.role === 'user');
  }, [messages]);

  const getLastAssistantMessage = useCallback(() => {
    return [...messages].reverse().find((msg) => msg.role === 'assistant');
  }, [messages]);

  return {
    messages,
    isLoaded,
    addMessage,
    updateMessage,
    deleteMessage,
    clearHistory,
    getLastUserMessage,
    getLastAssistantMessage,
    messageCount: messages.length,
  };
}

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export default useChatHistory;
